import { useCallback, useEffect, useRef, useState } from "react";
import { VoiceRecorder, isRecordingSupported, type RecordingResult } from "../../matrix/voice";

const LIVE_BARS = 28;
const MIN_DURATION_MS = 500;

export interface VoiceRecorderController {
  supported: boolean;
  recording: boolean;
  elapsedMs: number;
  /** Rolling amplitude buffer (0..1), newest last, length <= LIVE_BARS. */
  levels: number[];
  start: () => Promise<void>;
  /** Stop and resolve the recording, or null if it was too short to keep. */
  stop: () => Promise<RecordingResult | null>;
  cancel: () => void;
}

/**
 * Encapsulates the voice-recording lifecycle (MediaRecorder + live levels +
 * elapsed timer) so the composer can render a single, WhatsApp-style control.
 */
export function useVoiceRecorder(): VoiceRecorderController {
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  const recorderRef = useRef<VoiceRecorder | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTick = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
  };

  useEffect(
    () => () => {
      clearTick();
      recorderRef.current?.cancel();
    },
    [],
  );

  const pushLevel = useCallback((level: number) => {
    setLevels((prev) => {
      const next = [...prev, level];
      return next.length > LIVE_BARS ? next.slice(next.length - LIVE_BARS) : next;
    });
  }, []);

  const start = useCallback(async () => {
    const recorder = new VoiceRecorder({ onLevel: pushLevel });
    setLevels([]);
    setElapsedMs(0);
    await recorder.start();
    recorderRef.current = recorder;
    setRecording(true);
    const startedAt = Date.now();
    tickRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
  }, [pushLevel]);

  const stop = useCallback(async (): Promise<RecordingResult | null> => {
    const recorder = recorderRef.current;
    clearTick();
    setRecording(false);
    if (!recorder) return null;
    recorderRef.current = null;
    const result = await recorder.stop();
    setLevels([]);
    return result.durationMs < MIN_DURATION_MS ? null : result;
  }, []);

  const cancel = useCallback(() => {
    clearTick();
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setLevels([]);
    setRecording(false);
    setElapsedMs(0);
  }, []);

  return { supported: isRecordingSupported(), recording, elapsedMs, levels, start, stop, cancel };
}
